import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { pubchemAPI } from '../services/api';
import recentSearchesManager from '../utils/recentSearches';
import RecentSearchesSidebar from '../components/RecentSearchesSidebar';
import SEO from '../components/SEO';

function PubChem() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const resultsPerPage = 10;

  // Auto-search if query parameter is present
  useEffect(() => {
    const query = searchParams.get('q');
    if (query && query.trim()) {
      setSearchQuery(query);
      performSearch(query, 1);
    }
  }, [searchParams]);

  const performSearch = async (query, page = 1) => {
    setLoading(true);
    setError(null);

    try {
      const skip = (page - 1) * resultsPerPage;
      const response = await pubchemAPI.search(query, resultsPerPage, skip);
      setResults(response.data);
      setCurrentPage(page);

      // Calculate total pages based on results
      const totalResults = response.data?.results?.length || 0;
      setTotalPages(totalResults < resultsPerPage ? page : page + 1);

      // Save to recent searches (only on first page)
      if (page === 1) {
        recentSearchesManager.addSearch({
          query: query.trim(),
          database: 'pubchem',
          resultsCount: totalResults
        });
      }

    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    // Update URL with search query
    setSearchParams({ q: searchQuery.trim() });

    await performSearch(searchQuery.trim(), 1);
  };

  const handlePageChange = async (page) => {
    if (page < 1 || page > totalPages) return;
    await performSearch(searchQuery.trim(), page);
  };

  const renderResults = () => {
    if (!results) return null;

    if (results.error) {
      return (
        <div style={{ marginTop: '2rem' }}>
          <p style={{ color: '#e74c3c' }}>⚠️ {results.error}</p>
        </div>
      );
    }

    if (!results.compounds || results.compounds.length === 0) {
      return <p className="no-results">No compounds found for "{searchQuery}"</p>;
    }

    return (
      <div className="results-grid">
        {results.compounds.map((compound, index) => (
          <Link 
            to={`/pubchem/${compound.cid}`} 
            key={index} 
            className="clickable-card" 
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <div className="result-card hoverable-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                <h3 style={{ margin: 0 }}>{compound.name || compound.iupac_name || 'Unknown'}</h3>
                <span style={{ 
                  backgroundColor: '#667eea', 
                  color: 'white', 
                  padding: '0.25rem 0.5rem', 
                  borderRadius: '4px', 
                  fontSize: '0.75rem',
                  fontWeight: 'bold'
                }}>PubChem</span>
              </div>
            
            <div style={{ marginTop: '1rem' }}>
              <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Identifiers</h4>
              <p><strong>PubChem CID:</strong> <span style={{ color: '#3498db', fontWeight: '500' }}>{compound.cid}</span></p>
              {compound.iupac_name && compound.iupac_name !== compound.name && (
                <p><strong>IUPAC Name:</strong> {compound.iupac_name}</p>
              )}
            </div>

            {(compound.molecular_formula || compound.molecular_weight) && (
              <div style={{ marginTop: '1rem' }}>
                <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Molecular Properties</h4>
                {compound.molecular_formula && (
                  <p><strong>Formula:</strong> {compound.molecular_formula}</p>
                )}
                {compound.molecular_weight && (
                  <p><strong>Molecular Weight:</strong> {parseFloat(compound.molecular_weight).toFixed(2)} g/mol</p>
                )}
                {compound.xlogp !== undefined && compound.xlogp !== null && (
                  <p><strong>XLogP:</strong> {compound.xlogp}</p>
                )}
                {compound.tpsa && (
                  <p><strong>TPSA:</strong> {compound.tpsa} Ų</p>
                )}
                {compound.complexity && (
                  <p><strong>Complexity:</strong> {parseFloat(compound.complexity).toFixed(1)}</p>
                )}
              </div>
            )}

            {(compound.h_bond_donor_count !== undefined || compound.h_bond_acceptor_count !== undefined) && (
              <div style={{ marginTop: '1rem' }}>
                <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Hydrogen Bonding</h4>
                {compound.h_bond_donor_count !== undefined && (
                  <p><strong>H-Bond Donors:</strong> {compound.h_bond_donor_count}</p>
                )}
                {compound.h_bond_acceptor_count !== undefined && (
                  <p><strong>H-Bond Acceptors:</strong> {compound.h_bond_acceptor_count}</p>
                )}
                {compound.rotatable_bond_count !== undefined && (
                  <p><strong>Rotatable Bonds:</strong> {compound.rotatable_bond_count}</p>
                )}
              </div>
            )}

            {compound.canonical_smiles && (
              <div style={{ marginTop: '1rem' }}>
                <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Chemical Structure</h4>
                <p style={{ 
                  fontSize: '0.85em', 
                  fontFamily: 'monospace', 
                  backgroundColor: '#f8f9fa', 
                  padding: '0.5rem', 
                  borderRadius: '4px',
                  overflowX: 'auto',
                  wordBreak: 'break-all'
                }}>
                  <strong>SMILES:</strong> {compound.canonical_smiles.substring(0, 100)}
                  {compound.canonical_smiles.length > 100 ? '...' : ''}
                </p>
                {compound.inchi_key && (
                  <p style={{ 
                    fontSize: '0.85em', 
                    fontFamily: 'monospace', 
                    backgroundColor: '#f8f9fa', 
                    padding: '0.5rem', 
                    borderRadius: '4px',
                    marginTop: '0.5rem'
                  }}>
                    <strong>InChI Key:</strong> {compound.inchi_key}
                  </p>
                )}
              </div>
            )}

            {compound.charge !== 0 && compound.charge !== undefined && (
              <div style={{ marginTop: '1rem', padding: '0.5rem', backgroundColor: '#e8f4f8', borderRadius: '4px' }}>
                <strong>Formal Charge:</strong> {compound.charge > 0 ? '+' : ''}{compound.charge}
              </div>
            )}
            
            <div className="view-details-link">
              View Full Details →
            </div>
          </div>
          </Link>
        ))}
      </div>
    );
  };

  // SEO structured data
  const seoStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': 'PubChem Database Search',
    'description': 'Search PubChem for chemical compounds, properties, structures, and biological activities',
    'specialty': 'Chemical Informatics'
  };

  return (
    <div className="page-container">
      <SEO
        title="PubChem Database Search - Chemical Compounds & Properties"
        description="Search PubChem, the world's largest collection of freely accessible chemical information. Find chemical compounds, molecular properties, structures, and biological activities."
        keywords="PubChem, chemical compounds, molecular properties, chemical structures, biological activities, compound database, CID, molecular formula"
        canonical="/pubchem"
        structuredData={seoStructuredData}
      />
      <h1 className="page-title">PubChem Database</h1>
      <p className="page-description">
        Search PubChem, the world's largest collection of freely accessible chemical information. 
        Find compounds, their properties, structures, and biological activities.
      </p>

      <div className="search-section">
        <form onSubmit={handleSearch}>
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder="Enter compound name (e.g., aspirin, glucose, caffeine)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button type="submit" className="search-button" disabled={loading}>
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </form>
      </div>

      {error && <div className="error">Error: {error}</div>}
      
      {loading && <div className="loading">Loading...</div>}

      {!loading && results && (
        <div className="results-section">
          <div className="results-header">
            <h2>Results ({results.count || 0})</h2>
            {totalPages > 1 && (
              <div className="pagination-info">
                Page {currentPage} of {totalPages}
              </div>
            )}
          </div>
          {renderResults()}

          {totalPages > 1 && (
            <div className="pagination-controls">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="pagination-btn"
              >
                ← Previous
              </button>

              <div className="pagination-pages">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                  if (pageNum > totalPages) return null;

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      disabled={loading}
                      className={`pagination-btn ${pageNum === currentPage ? 'active' : ''}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
                className="pagination-btn"
              >
                Next →
              </button>
            </div>
          )}
        </div>
      )}

      <RecentSearchesSidebar database="pubchem" />
    </div>
  );
}

export default PubChem;
